import { useState, useRef, useEffect, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import axios from "axios";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { safeNext } from "@shared/lib/staffSession";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffLogin = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  // F-W: arriving from an expired session → say so, then return the user
  // to the page they were on (staff paths only; see `safeNext`).
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("expired") === "1";
  const returnTo = safeNext(searchParams.get("next"));
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  // Bounce a session that is ALREADY authenticated on arrival (e.g. the
  // user hit /staff/login with a live cookie). Runs on mount only — post-
  // submit navigation, including the F-A first-login redirect to the
  // one-time-setup screen, is owned by handleSubmit. Reading auth state via a
  // mount-captured ref (rather than an [isAuthenticated] dep) keeps login()'s
  // token-set inside handleSubmit from re-firing this effect and racing that
  // redirect (it used to clobber the mustChangePassword path). `navigate` is
  // stable, so the effect stays mount-only without an eslint-disable.
  const authedOnMountRef = useRef(isAuthenticated);
  useEffect(() => {
    if (authedOnMountRef.current) {
      navigate("/staff/exhibitions");
    }
  }, [navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/staff/login`, formData);
      const {
        token,
        username,
        role,
        mustChangePassword,
        teamIds,
        departmentId,
      } = response.data;
      login(token, username, role, { teamIds, departmentId });
      if (mustChangePassword) {
        // F-A first-login gate: route to the one-time-setup screen
        // instead of the home page until the admin-set password is changed.
        toast.message("Please set a new password to continue");
        navigate("/staff/change-password?firstLogin=1");
      } else {
        toast.success("Login successful");
        navigate(returnTo ?? "/staff/exhibitions");
      }
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background px-4"
      data-testid="staff-login-page"
    >
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Staff Login</h1>
            <p className="text-muted-foreground mt-2">
              Museum Exhibit Management
            </p>
          </div>
        </div>

        {sessionExpired && (
          <p
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            role="status"
            data-testid="session-expired-message"
          >
            Your session has expired. Please log in again.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              required
              data-testid="username-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
              data-testid="password-input"
              className="h-12"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>

        <div className="text-center">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="visitor-view-link"
          >
            Back to Visitor View
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
